import { useState, useEffect, useCallback } from 'react';
import AdminLogin from './Login';
import AdminLayout from './AdminLayout';
import AdminDashboard from './Dashboard';
import AdminBatches from './Batches';
import AdminReports from './Reports';
import AdminCacheStats from './CacheStats';
import AdminApiKeys from './ApiKeys';
import AdminSettings from './Settings';
import AdminIssues from './Issues';
import AdminLocationBilling from './LocationBilling';
import AdminBillingRates from './BillingRates';
import AdminEmailTemplates from './EmailTemplates';
import AdminUsers from './AdminUsers';
import AdminTeamMembers from './TeamMembers';
import AdminSmsDashboard from './SmsDashboard';
import AdminCacheManagement from './CacheManagement';
import Affiliates from './Affiliates';
import Prospects from './Prospects';
import AdminNotifications from './Notifications';

// Admin user type
interface AdminUser {
  id: string;
  email: string;
  fullName: string | null;
  role: 'super_admin' | 'admin' | 'viewer';
}

// Admin pages
type AdminPage = 'dashboard' | 'sms-analytics' | 'batches' | 'reports' | 'cache' | 'cache-management' | 'api-keys' | 'settings' | 'issues' | 'billing' | 'billing-rates' | 'email-templates' | 'notifications' | 'users' | 'team-members' | 'affiliates' | 'prospects';

export default function AdminApp() {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState<AdminUser | null>(null);
  const [currentPage, setCurrentPage] = useState<AdminPage>('dashboard');

  // Check session on mount
  useEffect(() => {
    checkSession();

    // Parse initial page from URL
    const path = window.location.pathname.replace('/admin', '').replace('/', '');
    if (path && isValidPage(path)) {
      setCurrentPage(path as AdminPage);
    }
  }, []);

  useEffect(() => {
    document.title = 'OAK Admin Portal';
  }, []);

  const checkSession = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/session`, {
        credentials: 'include',
      });

      const data = (await response.json()) as any;

      if (data.success && data.authenticated) {
        setAuthenticated(true);
        setUser(data.user);
      } else {
        setAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      console.error('[AdminApp] Session check error:', error);
      setAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (userData: AdminUser) => {
    setAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = async () => {
    try {
      await fetch(`${import.meta.env.VITE_API_URL}/admin/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('[AdminApp] Logout error:', error);
    }

    setAuthenticated(false);
    setUser(null);
    setCurrentPage('dashboard');
  };

  const navigateTo = useCallback((page: AdminPage) => {
    setCurrentPage(page);
    // Update URL without reload. On admin.* subdomain, no /admin prefix is
    // needed; the hostname itself selects the admin app. On the legacy
    // oak-list-cleaner.pages.dev/admin path, keep the prefix.
    const onAdminSubdomain = window.location.hostname.startsWith('admin.');
    const prefix = onAdminSubdomain ? '' : '/admin';
    const newPath = page === 'dashboard' ? prefix || '/' : `${prefix}/${page}`;
    window.history.pushState({}, '', newPath);
  }, []);

  // Handle browser back/forward
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname.replace('/admin', '').replace('/', '');
      if (!path || path === '') {
        setCurrentPage('dashboard');
      } else if (isValidPage(path)) {
        setCurrentPage(path as AdminPage);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  // Login screen
  if (!authenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  // Render current page
  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <AdminDashboard />;
      case 'sms-analytics':
        return <AdminSmsDashboard />;
      case 'batches':
        return <AdminBatches />;
      case 'reports':
        return <AdminReports />;
      case 'cache':
        return <AdminCacheStats />;
      case 'cache-management':
        return <AdminCacheManagement />;
      case 'api-keys':
        return <AdminApiKeys />;
      case 'settings':
        return <AdminSettings />;
      case 'issues':
        return <AdminIssues role={user?.role} />;
      case 'billing':
        return <AdminLocationBilling />;
      case 'billing-rates':
        return <AdminBillingRates />;
      case 'email-templates':
        return <AdminEmailTemplates />;
      case 'notifications':
        return <AdminNotifications />;
      case 'users':
        return <AdminUsers />;
      case 'team-members':
        return <AdminTeamMembers />;
      case 'affiliates':
        return <Affiliates />;
      case 'prospects':
        return <Prospects />;
      default:
        return <AdminDashboard />;
    }
  };

  return (
    <AdminLayout
      user={user}
      currentPage={currentPage}
      onNavigate={navigateTo}
      onLogout={handleLogout}
    >
      {renderPage()}
    </AdminLayout>
  );
}

function isValidPage(page: string): boolean {
  const validPages = ['dashboard', 'sms-analytics', 'batches', 'reports', 'cache', 'cache-management', 'api-keys', 'settings', 'issues', 'billing', 'billing-rates', 'email-templates', 'notifications', 'users', 'team-members', 'affiliates', 'prospects'];
  return validPages.includes(page);
}
